<template>
    <div class="animated fadeIn">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-users"></i> Usuários 3C Plus
              </h4>
            </div>
            <div class="card-body">
              <div class="row mb-3">
                <div class="col-md-12">
                  <button
                    class="btn btn-primary"
                    @click="showAddModal = true"
                    :disabled="!hasConfig"
                  >
                    <i class="fa fa-plus"></i> Adicionar Usuário
                  </button>
                  <small v-if="!hasConfig" class="text-muted ml-2">
                    Configure primeiro o domínio da 3C Plus
                  </small>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Usuário do Sistema</th>
                      <th>Usuário 3C Plus</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="user.id">
                      <td>{{ user.systemUserName }}</td>
                      <td>{{ user.threecUsername }}</td>
                      <td>
                        <b-badge :variant="user.enabled ? 'success' : 'secondary'">
                          {{ user.enabled ? 'Habilitado' : 'Desabilitado' }}
                        </b-badge>
                      </td>
                      <td>
                        <button
                          class="btn btn-sm btn-outline-primary mr-1"
                          @click="editUser(user)"
                          title="Editar"
                        >
                          <i class="fa fa-edit"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteUser(user.id)"
                          title="Excluir"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="users.length === 0">
                      <td colspan="4" class="text-center text-muted">
                        Nenhum usuário cadastrado
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal para adicionar/editar usuário -->
      <b-modal
        v-model="showAddModal"
        :title="editingUser ? 'Editar Usuário 3C Plus' : 'Adicionar Usuário 3C Plus'"
        size="md"
        @hidden="resetForm"
      >
        <Forms
          ref="frmUser"
          :title="''"
          :showClearButton="false"
          :showSaveButton="false"
          :showBackButton="false"
          :isModal="true"
        >
          <div class="form-group row">
            <div class="col-md-12">
              <label for="systemUserSelect">
                <small v-if="form.systemUserId.required" class="text-danger">*</small>
                Usuário do Sistema:
              </label>
              <b-form-select
                v-model="form.systemUserId.value"
                :options="systemUsers"
                :state="form.systemUserId.state"
                :disabled="!!editingUser"
              >
                <template slot="first">
                  <option :value="null">Selecione um usuário</option>
                </template>
              </b-form-select>
            </div>
          </div>

          <div class="form-group row">
            <div class="col-md-12">
              <label for="threeCUserInput">
                <small v-if="form.threeCUser.required" class="text-danger">*</small>
                Usuário 3C Plus:
              </label>
              <b-form-input
                type="text"
                id="threeCUserInput"
                v-model="form.threeCUser.value"
                :maxlength="form.threeCUser.maxlength"
                :required="form.threeCUser.required"
                :state="form.threeCUser.state"
                placeholder="Digite o usuário da 3C Plus"
              />
            </div>
          </div>

          <div class="form-group row">
            <div class="col-md-12">
              <label for="threeCPasswordInput">
                <small v-if="form.threeCPassword.required" class="text-danger">*</small>
                Senha 3C Plus:
              </label>
              <b-form-input
                type="password"
                id="threeCPasswordInput"
                v-model="form.threeCPassword.value"
                :maxlength="form.threeCPassword.maxlength"
                :required="form.threeCPassword.required"
                :state="form.threeCPassword.state"
                placeholder="Digite a senha da 3C Plus"
              />
            </div>
          </div>

          <div class="form-group row">
            <div class="col-md-12">
              <b-form-checkbox
                v-model="form.enabled.value"
                switch
                :value="true"
                :unchecked-value="false"
              >
                Habilitar usuário para 3C Plus
              </b-form-checkbox>
            </div>
          </div>
        </Forms>

        <template slot="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showAddModal = false">
            Cancelar
          </button>
          <button type="button" class="btn btn-primary" @click="saveUser">
            {{ editingUser ? 'Atualizar' : 'Adicionar' }}
          </button>
        </template>
      </b-modal>
    </div>
  </template>

<script>
import ActionsForm from '@/shared/mixins/actionsForm'
import threeCPlusUserForm from './threeCPlusUser-form'
import ThreeCPlusRepository from '@/shared/http/repositories/configurations/threeCPlus'
import { toast, loading } from '@/shared/utils/UIHelper'

export default {
  name: 'ThreeCPlusUsers',
  mixins: [ActionsForm],
  data () {
    return {
      form: threeCPlusUserForm,
      users: [],
      systemUsers: [],
      showAddModal: false,
      editingUser: null,
      hasConfig: false
    }
  },
  methods: {
    async onLoad () {
      try {
        loading.push()
        await this.loadUsers()
        await this.loadSystemUsers()
        await this.checkConfig()
      } catch (error) {
        this.onException(error)
      } finally {
        loading.pop()
      }
    },
    async loadUsers () {
      try {
        const response = await ThreeCPlusRepository.getUsers({
          page: 0,
          size: 100
        })
        if (response.status === 200) {
          this.users = response.data.content || []
        }
      } catch (error) {
        this.users = []
      }
    },
    async loadSystemUsers () {
      try {
        const response = await ThreeCPlusRepository.getSystemUsers()

        if (response.status === 200) {
          const users = response.data.content || response.data || []

          this.systemUsers = users.map(user => ({
            value: user.id,
            text: user.username || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()
          }))
        } else {
          this.systemUsers = []
        }
      } catch (error) {
        this.systemUsers = []
      }
    },
    async checkConfig () {
      try {
        const response = await ThreeCPlusRepository.getConfig()
        this.hasConfig = response.status === 200 && response.data.domain
      } catch (error) {
        this.hasConfig = false
      }
    },
    editUser (user) {
      this.editingUser = user
      this.form.systemUserId.value = user.systemUserId
      this.form.threeCUser.value = user.threecUsername
      this.form.threeCPassword.value = user.threecPassword
      this.form.enabled.value = user.enabled
      this.showAddModal = true
    },
    async saveUser () {
      try {
        if (!this.validateForm()) {
          return
        }

        loading.push()

        const userData = {
          systemUserId: this.form.systemUserId.value,
          threecUsername: this.form.threeCUser.value,
          threecPassword: this.form.threeCPassword.value,
          enabled: this.form.enabled.value
        }

        let response
        if (this.editingUser) {
          response = await ThreeCPlusRepository.updateUser(this.editingUser.id, userData)
        } else {
          response = await ThreeCPlusRepository.createUser(userData)
        }

        if (response.status === 200 || response.status === 201) {
          toast.success('Usuário salvo com sucesso!')
          this.showAddModal = false
          await this.loadUsers()
        } else {
          toast.error('Erro ao salvar usuário', 'Erro')
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          const errorData = error.response.data
          if (errorData.errors) {
            errorData.errors.forEach(err => {
              toast.error(err.message, 'Erro de Validação')
            })
          } else {
            toast.error(errorData.message || 'Dados inválidos', 'Erro de Validação')
          }
        } else if (error.response && error.response.status === 409) {
          toast.error('Usuário já existe ou está vinculado a outro usuário do sistema', 'Conflito')
        } else if (error.response && error.response.status === 403) {
          toast.error('Você não tem permissão para gerenciar usuários', 'Acesso Negado')
        } else if (error.response && error.response.status === 401) {
          toast.error('Sessão expirada. Faça login novamente', 'Não Autorizado')
        } else {
          this.onException(error)
        }
      } finally {
        loading.pop()
      }
    },
    async deleteUser (userId) {
      if (confirm('Tem certeza que deseja excluir este usuário?')) {
        try {
          loading.push()

          const response = await ThreeCPlusRepository.deleteUser(userId)

          if (response.status === 204) {
            toast.success('Usuário excluído com sucesso!')
            await this.loadUsers()
          } else {
            toast.error('Erro ao excluir usuário', 'Erro')
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            toast.error('Usuário não encontrado', 'Não Encontrado')
          } else if (error.response && error.response.status === 403) {
            toast.error('Você não tem permissão para excluir usuários', 'Acesso Negado')
          } else if (error.response && error.response.status === 401) {
            toast.error('Sessão expirada. Faça login novamente', 'Não Autorizado')
          } else {
            this.onException(error)
          }
        } finally {
          loading.pop()
        }
      }
    },
    resetForm () {
      this.editingUser = null
      this.form.systemUserId.value = null
      this.form.threeCUser.value = ''
      this.form.threeCPassword.value = ''
      this.form.enabled.value = false
    },
    validateForm () {
      let isValid = true

      if (!this.form.systemUserId.value) {
        this.form.systemUserId.state = false
        isValid = false
      } else {
        this.form.systemUserId.state = null
      }

      if (!this.form.threeCUser.value) {
        this.form.threeCUser.state = false
        isValid = false
      } else {
        this.form.threeCUser.state = null
      }

      if (!this.form.threeCPassword.value) {
        this.form.threeCPassword.state = false
        isValid = false
      } else {
        this.form.threeCPassword.state = null
      }

      return isValid
    }
  },
  mounted () {
    this.onLoad()
  }
}
</script>



// WEBPACK FOOTER //
// src/views/private/configurations/parametros/threeCPlus/ThreeCPlusUsers.vue